"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@workspace/ui/lib/utils";
import { cva, VariantProps } from "class-variance-authority";
import { motion } from "motion/react";
import { Badge } from "./badge";

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

const tabsListVariants = cva(
  "text-muted-foreground inline-flex w-fit items-center justify-center",
  {
    variants: {
      variant: {
        default: "bg-muted h-9 p-[3px] rounded-lg",
        "d-default": "h-fit gap-8",
        "d-pill": "h-12 w-full gap-1.5 rounded-2xl bg-[#F7F7F7] p-2",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TabsList({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(tabsListVariants({ variant, className }))}
      {...props}
    />
  );
}

const tabsTriggerVariants = cva(
  "inline-flex flex-1 items-center justify-center gap-1.5 font-medium whitespace-nowrap transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50  [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "text-foreground text-sm  focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring data-[state=active]:bg-background dark:data-[state=active]:text-foreground dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 dark:text-muted-foreground h-[calc(100%-1px)] border border-transparent rounded-md  px-2 py-1 focus-visible:ring-[3px] focus-visible:outline-1 data-[state=active]:shadow-sm",
        "d-default":
          "group box-border text-d-black text-base leading-[22px] border-b-2 border-transparent data-[state=active]:border-b-[#ffd602] pb-5 cursor-pointer font-medium text-base",
        "d-pill":
          "h-8 flex-1 cursor-pointer rounded-[12px] text-sm/5 font-semibold text-[#797268] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=active]:bg-white data-[state=active]:text-d-black data-[state=active]:shadow-[0_4px_4px_rgba(41,37,36,.12)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TabsTrigger({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant, className }))}
      {...props}
    />
  );
}

function AnimatedTabTrigger({
  value,
  name,
  color,
  count,
}: {
  value: string;
  name: string;
  color?: string;
  count?: number;
}) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <TabsTrigger
      value={value}
      className="flex items-center pb-5 gap-0"
      variant="d-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {color && (
        <span
          style={{ "--tc": color } as React.CSSProperties}
          className="w-2.5 h-2.5 rounded-full border-2 border-[var(--tc)] mr-1.5"
        />
      )}
      <div
        style={{
          justifyContent: hovered ? "flex-start" : "flex-end",
        }}
        className="flex flex-col h-[22px] overflow-hidden group-data-[state=inactive]:text-d-gray leading-[22px] font-medium"
      >
        <motion.span
          layout
          transition={{ duration: 0.1 }}
          className="whitespace-nowrap h-[22px]"
        >
          {name}
        </motion.span>
        <motion.span
          layout
          transition={{ duration: 0.1 }}
          className="whitespace-nowrap h-[22px]"
        >
          {name}
        </motion.span>
      </div>
      {!!count && (
        <Badge
          variant="secondary"
          className="bg-[rgba(2,6,23,.08)] font-medium text-[13px] px-0.5 h-5 min-w-5 rounded-[4px] ml-2"
        >
          {count}
        </Badge>
      )}
    </TabsTrigger>
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, AnimatedTabTrigger };
